import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import websitePurgeService from '../services/websitePurgeService';
import {
  DEFAULT_RATE,
  KEEP_RATE,
  RATE_CHOICES,
  resumeRateArg,
  resumeRateChoices,
  actionLabel,
  activeJobLabel,
  blockerLabel,
  canConfirm,
  countLabel,
  expectedConfirmation,
  isLive,
  isTerminal,
  leftoverLabel,
  pauseLabel,
  progressLabel,
  refusalLabel,
  searchSweepLabel,
  splitInventory,
  statusLabel,
  totalLabel
} from '../lib/websitePurge';

// « Supprimer intégralement ce site » (hosting-migration B2_09) — fenêtre RÉUTILISABLE :
// liste chiffrée → périmètre en toutes lettres → confirmation par saisie du nom → suivi.
// Elle ne prend qu'un `websiteId` et ne dépend PAS de la fiche du site : elle peut s'ouvrir
// depuis un autre écran (prévu : « Google Sites — suivi du parc »). Seul appelant à ce jour : Website.js.
//
// ⛔ Rien à voir avec « Confirmer suppression communauté » (deletionService) : celui-là anonymise
// et garde la donnée ; celui-ci efface. Ne pas fusionner les deux.
//
// Props : websiteId (string), open (bool), onClose(), onPurged(websiteId) — appelé une fois quand
// la suppression se termine (la liste appelante retire alors la ligne).

const POLL_MS = 5000;

class WebsitePurgeDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.pollTimer = null;
    this.notifiedDone = false;
  }

  initialState() {
    return {
      view: 'loading', // loading | inventory | tracking | error
      inventory: null,
      status: null,
      error: null,
      typed: '',
      rate: DEFAULT_RATE,
      resumeRate: KEEP_RATE,
      busy: false,
      actionError: null
    };
  }

  componentDidMount() {
    if (this.props.open) this.load();
  }

  componentDidUpdate(prev) {
    if (this.props.open && (!prev.open || prev.websiteId !== this.props.websiteId)) {
      this.notifiedDone = false;
      this.setState(this.initialState(), () => this.load());
    }
    if (!this.props.open && prev.open) this.stopPolling();
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  // Une suppression existe déjà pour ce site (en cours, arrêtée, terminée) → on montre le SUIVI.
  // Sinon (404 = état normal) → l'inventaire.
  load() {
    const { websiteId } = this.props;
    websitePurgeService
      .getStatus(websiteId)
      .then((status) => {
        if (this.props.websiteId !== websiteId) return;
        if (isTerminal(status.status) && status.status === 'failed') return this.loadInventory();
        this.setState({ view: 'tracking', status });
        this.afterStatus(status);
      })
      .catch((err) => {
        if (websitePurgeService.isNoPurge(err)) return this.loadInventory();
        this.setState({ view: 'error', error: websitePurgeService.parseApiError(err) });
      });
  }

  loadInventory() {
    const { websiteId } = this.props;
    this.setState({ view: 'loading' });
    websitePurgeService
      .getInventory(websiteId)
      .then((inventory) => {
        if (this.props.websiteId !== websiteId) return;
        this.setState({ view: 'inventory', inventory });
      })
      .catch((err) => this.setState({ view: 'error', error: websitePurgeService.parseApiError(err) }));
  }

  afterStatus(status) {
    if (isLive(status.status)) {
      this.stopPolling();
      this.pollTimer = setTimeout(() => this.poll(), POLL_MS);
    } else if (['done', 'done_with_leftovers'].includes(status.status) && !this.notifiedDone) {
      this.notifiedDone = true;
      if (this.props.onPurged) this.props.onPurged(this.props.websiteId);
    }
  }

  poll() {
    if (!this.props.open) return;
    websitePurgeService
      .getStatus(this.props.websiteId)
      .then((status) => {
        // ⚠ On ne démonte jamais le contenu pendant un rafraîchissement (piège B5_01).
        this.setState({ status, actionError: null });
        this.afterStatus(status);
      })
      .catch((err) => {
        this.setState({ actionError: websitePurgeService.parseApiError(err) });
        this.pollTimer = setTimeout(() => this.poll(), POLL_MS * 2);
      });
  }

  run(action) {
    this.setState({ busy: true, actionError: null });
    return action()
      .then(() => websitePurgeService.getStatus(this.props.websiteId))
      .then((status) => {
        this.setState({ view: 'tracking', status, busy: false, typed: '', resumeRate: KEEP_RATE });
        this.afterStatus(status);
      })
      .catch((err) => this.setState({ busy: false, actionError: websitePurgeService.parseApiError(err) }));
  }

  start = () => this.run(() => websitePurgeService.start(this.props.websiteId, { maxDocsPerSecond: this.state.rate }));

  // Reprise : par défaut AUCUN débit envoyé — celui du job est conservé (l'écraser par le défaut
  // de l'écran ferait repartir à 1 000/s un site qu'on avait volontairement lancé à 250/s). Un
  // débit n'est envoyé que s'il a été CHOISI sur la vue « arrêtée » (accélérer après observation).
  resume = () =>
    this.run(() => websitePurgeService.start(this.props.websiteId, { maxDocsPerSecond: resumeRateArg(this.state.resumeRate) }));

  stop = () => this.run(() => websitePurgeService.stop(this.props.websiteId));

  renderError(error, testid) {
    if (!error) return null;
    const expired = error.code === websitePurgeService.SESSION_EXPIRED;
    return (
      <Alert severity="error" data-testid={testid} data-code={error.code || error.status || ''}>
        {expired ? error.msg : `${error.status ? `Erreur ${error.status} — ` : ''}${refusalLabel(error)}`}
        {activeJobLabel(error.activeJob) && <div data-testid="website-purge-active-job" data-website-id={error.activeJob.website_id}>{activeJobLabel(error.activeJob)}</div>}
        {Array.isArray(error.blockers) && error.blockers.map((b, i) => <div key={i}>• {blockerLabel(b)}</div>)}
      </Alert>
    );
  }

  renderRows(rows, testid) {
    return (
      <Table size="small" data-testid={testid}>
        <TableHead>
          <TableRow>
            <TableCell>Donnée</TableCell>
            <TableCell align="right">Nombre</TableCell>
            <TableCell>Sort</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((e) => (
            <TableRow key={`${e.model}.${e.path}`} data-action={e.action} data-count-state={e.count.state}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{e.model}.{e.path}</TableCell>
              <TableCell align="right">{countLabel(e.count)}</TableCell>
              <TableCell>{actionLabel(e.action)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  renderInventory() {
    const { inventory, typed, rate, busy } = this.state;
    const { deleted, kept, hiddenEmpty } = splitInventory(inventory);
    const expected = expectedConfirmation(inventory.website);
    const blocked = !inventory.can_purge;
    const search = (inventory.outside_mongo && inventory.outside_mongo.search_index) || [];
    const images = inventory.outside_mongo && inventory.outside_mongo.images;
    return (
      <Stack spacing={2} data-testid="website-purge-inventory" data-can-purge={String(!blocked)}>
        <Alert severity="warning">
          <b>Action irréversible.</b> Aucune sauvegarde n’est prise. {inventory.scope_note}
        </Alert>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip size="small" label={`Statut : ${inventory.website.status || '—'}`} />
          <Chip size="small" label={`Plan : ${inventory.billing.plan || inventory.billing.rights}`} color={inventory.billing.looks_paid ? 'warning' : 'default'} />
          {inventory.billing.has_stripe_customer && <Chip size="small" color="warning" label="Identifiant Stripe présent — vérifié au lancement" />}
          <Chip size="small" label={`Réplication : ${inventory.replication_guard.state}`} />
        </Stack>
        <Typography variant="body2">
          <b>{inventory.website.name || '—'}</b> — {inventory.website.url || '—'} — <span style={{ fontFamily: 'monospace' }}>{inventory.website._id}</span>
        </Typography>
        {inventory.blockers.length > 0 && (
          <Alert severity="error" data-testid="website-purge-blockers">
            Suppression impossible :
            {inventory.blockers.map((b, i) => <div key={i}>• {blockerLabel(b)}</div>)}
          </Alert>
        )}
        <Typography variant="subtitle2" data-testid="website-purge-total">
          Sera supprimé : {totalLabel(inventory.totals)}
          {inventory.totals.time_budget_exhausted ? ' — certaines lignes n’ont pas eu le temps d’être comptées' : ''}
        </Typography>
        {this.renderRows(deleted, 'website-purge-rows-deleted')}
        <Typography variant="body2">
          Hors base : moteur de recherche — {search.map((s) => `${s.index} ${countLabel(s)}`).join(' · ') || '—'} ; images —{' '}
          {images && images.state === 'ok' ? `${countLabel({ state: 'exact', value: images.dir_entries })} fichier(s)${images.logo ? ' + logo' : ''}` : (images && images.state) || '—'} ; fichiers joints aux commentaires — <b>conservés</b>.
        </Typography>
        <Typography variant="subtitle2">Conservé (non supprimé)</Typography>
        {this.renderRows(kept, 'website-purge-rows-kept')}
        {hiddenEmpty > 0 && <Typography variant="caption">{hiddenEmpty} autres collections : aucun document de ce site.</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            size="small"
            label="Débit"
            value={rate}
            onChange={(e) => this.setState({ rate: Number(e.target.value) })}
            sx={{ minWidth: 280 }}
            inputProps={{ 'data-testid': 'website-purge-rate' }}
          >
            {RATE_CHOICES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            fullWidth
            label={`Pour confirmer, tape : ${expected}`}
            value={typed}
            disabled={blocked || busy}
            onChange={(e) => this.setState({ typed: e.target.value })}
            inputProps={{ 'data-testid': 'website-purge-confirm-input' }}
          />
        </Stack>
      </Stack>
    );
  }

  renderTracking() {
    const { status, resumeRate, busy } = this.state;
    const currentRate = status.settings && status.settings.max_docs_per_second;
    const sweep = searchSweepLabel(status.es_sweep_deleted);
    const pause = pauseLabel(status.paused_reason);
    const ended = ['done', 'done_with_leftovers'].includes(status.status);
    return (
      <Stack spacing={2} data-testid="website-purge-tracking" data-status={status.status} data-resumable={String(Boolean(status.resumable))}>
        <Typography variant="body2">
          <b>{(status.website && status.website.name) || '—'}</b> — {(status.website && status.website.url) || '—'}
        </Typography>
        <Alert severity={ended ? 'success' : status.status === 'interrupted' || status.status === 'failed' ? 'error' : 'info'}>
          <b>{statusLabel(status.status)}</b>
          {pause ? ` — ${pause}` : ''}
          {status.stale ? ' — aucun signe de vie récent : tu peux reprendre' : ''}
          {status.error ? ` — ${status.error}` : ''}
        </Alert>
        <Typography variant="subtitle2" data-testid="website-purge-progress">{progressLabel(status.progress)}</Typography>
        {typeof currentRate === 'number' && !ended && (
          <Typography variant="caption" data-testid="website-purge-rate-cap" data-rate={currentRate}>
            Débit plafonné à {countLabel({ state: 'exact', value: currentRate })}/s
          </Typography>
        )}
        {status.current_step && (
          <Typography variant="body2" data-testid="website-purge-current-step">
            En cours : <span style={{ fontFamily: 'monospace' }}>{status.current_step.key}</span> — {countLabel({ state: 'exact', value: status.current_step.deleted })} supprimés
            {status.current_step.estimate ? ` sur ${countLabel(status.current_step.estimate)}` : ''}
          </Typography>
        )}
        <Typography variant="body2" data-testid="website-purge-sweep" data-sweep-state={sweep.state}>{sweep.label}</Typography>
        {status.resumable && (
          <TextField
            select
            size="small"
            label="Débit à la reprise"
            value={resumeRate}
            disabled={busy}
            onChange={(e) => this.setState({ resumeRate: e.target.value })}
            sx={{ maxWidth: 320 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'website-purge-resume-rate', 'data-resume-rate': String(resumeRate) }}
          >
            {resumeRateChoices(currentRate).map((c) => <MenuItem key={String(c.value)} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
        )}
        {Array.isArray(status.leftovers) && status.leftovers.length > 0 && (
          <Box data-testid="website-purge-leftovers">
            <Typography variant="subtitle2">Conservé ou laissé — c’est attendu</Typography>
            {status.leftovers.map((l, i) => <Typography key={i} variant="caption" display="block">• {leftoverLabel(l)}</Typography>)}
          </Box>
        )}
        <Typography variant="caption">{status.scope_note}</Typography>
      </Stack>
    );
  }

  render() {
    const { open, onClose } = this.props;
    const { view, inventory, status, typed, busy, error, actionError } = this.state;
    const live = view === 'tracking' && status && isLive(status.status);
    const resumable = view === 'tracking' && status && status.resumable;
    return (
      <Dialog open={Boolean(open)} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" data-testid="website-purge-dialog" data-state={view}>
        <DialogTitle>Supprimer intégralement ce site</DialogTitle>
        <DialogContent dividers>
          {view === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
          {view === 'error' && this.renderError(error, 'website-purge-error')}
          {view === 'inventory' && inventory && this.renderInventory()}
          {view === 'tracking' && status && this.renderTracking()}
          <Box sx={{ mt: 2 }}>{this.renderError(actionError, 'website-purge-action-error')}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Fermer</Button>
          {view === 'inventory' && inventory && (
            <Button
              color="error"
              variant="contained"
              disabled={busy || !inventory.can_purge || !canConfirm(typed, inventory.website)}
              onClick={this.start}
              data-testid="website-purge-confirm"
            >
              Supprimer définitivement
            </Button>
          )}
          {live && !status.stale && (
            <Button color="warning" variant="outlined" disabled={busy || status.stop_requested} onClick={this.stop} data-testid="website-purge-stop">
              {status.stop_requested ? 'Arrêt demandé…' : 'Arrêter'}
            </Button>
          )}
          {resumable && (
            <Button color="error" variant="contained" disabled={busy} onClick={this.resume} data-testid="website-purge-resume">
              Reprendre
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

export default WebsitePurgeDialog;
